import React, { useState } from "react"
import { useNavigate, Link } from "@tanstack/react-router"
import {
  useMyAssignments,
  useAssignmentDetail,
  useAttemptReview,
} from "@/lib/queries"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { Button } from "@/components/ui/button"
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Loader2, Pencil } from "lucide-react"
import { FullNameDialog } from "@/components/FullNameDialog"
import { useAuth } from "@/contexts/AuthContext"
import type { AttemptSummary, StaffAssignmentRow } from "@/lib/types"
import { cn } from "@/lib/utils"

const formatDate = (dateString: string | null) => {
  if (!dateString) return "—"
  return new Date(dateString).toLocaleDateString("en-CA")
}

type AssignmentStatus = StaffAssignmentRow["status"]

type StatusConfig = {
  label: string
  className: string
}

const STATUS_CONFIG: Record<AssignmentStatus, StatusConfig> = {
  passed: {
    label: "Passed",
    className:
      "border-transparent bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-400",
  },
  not_passed: {
    label: "Not Passed",
    className:
      "border-transparent bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400",
  },
  in_progress: {
    label: "In Progress",
    className:
      "border-transparent bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400",
  },
  not_started: {
    label: "Not Started",
    className: "border-transparent bg-secondary text-secondary-foreground",
  },
}

// Only once the organization caps attempts, and only until the person passes:
// after that the count no longer tells them anything.
function AttemptsRemaining({ row }: { row: StaffAssignmentRow }) {
  if (row.status === "passed" || row.attempts_remaining === null || row.max_attempts === null) {
    return null
  }
  if (row.attempts_remaining === 0) {
    return (
      <p className="mt-1 text-xs text-destructive">
        No attempts remaining. Contact your manager.
      </p>
    )
  }
  return (
    <p className="mt-1 text-xs text-muted-foreground">
      {row.attempts_remaining} of {row.max_attempts} attempt
      {row.max_attempts === 1 ? "" : "s"} remaining
    </p>
  )
}

function ResultBadge({ status }: { status: AssignmentStatus }) {
  const { label, className } = STATUS_CONFIG[status]
  return (
    <Badge className={cn("text-xs font-semibold", className)}>{label}</Badge>
  )
}

// Attempt history. Overall result only — the answer key is never shown.
function AttemptHistory({ assignmentId }: { assignmentId: number }) {
  const { data, isLoading } = useAssignmentDetail(assignmentId)
  const [reviewing, setReviewing] = useState<AttemptSummary | null>(null)

  if (isLoading) {
    return <p className="px-4 py-3 text-sm text-muted-foreground">Loading history…</p>
  }
  if (!data || data.attempts.length === 0) {
    return <p className="px-4 py-3 text-sm text-muted-foreground">No attempts yet.</p>
  }

  return (
    <>
      <table className="w-full text-sm">
      <thead>
        <tr className="text-left text-xs uppercase tracking-wide text-muted-foreground">
          <th className="px-4 py-2 font-medium">Attempt</th>
          <th className="px-4 py-2 font-medium">Submitted</th>
          <th className="px-4 py-2 font-medium">Version</th>
          <th className="px-4 py-2 font-medium">Score</th>
          <th className="px-4 py-2 font-medium">Result</th>
          <th className="px-4 py-2 font-medium sr-only">Review</th>
        </tr>
      </thead>
      <tbody>
        {data.attempts.map((a) => (
          <tr key={a.id} className="border-t">
            <td className="px-4 py-2">{a.number}</td>
            <td className="px-4 py-2 text-muted-foreground">{formatDate(a.submitted_at)}</td>
            <td className="px-4 py-2 text-muted-foreground">{a.version_label}</td>
            <td className="px-4 py-2 text-muted-foreground">
              {a.score !== null ? `${a.score}%` : "—"}
            </td>
            <td className="px-4 py-2">
              {a.status === "in_progress"
                ? "In Progress"
                : a.passed
                  ? "Passed"
                  : "Not Passed"}
            </td>
            <td className="px-4 py-2 text-right">
              {a.status !== "in_progress" && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-auto px-2 py-1 text-xs"
                  onClick={() => setReviewing(a)}
                >
                  Review answers
                </Button>
              )}
            </td>
          </tr>
        ))}
      </tbody>
      </table>
      <AttemptReviewDialog
        attempt={reviewing}
        onClose={() => setReviewing(null)}
      />
    </>
  )
}

// Their own answers, with the wrong ones marked. Which option was correct is
// deliberately absent: retakes are unlimited, so showing the key would make
// the next attempt a memory test of this screen. A two-option question gives
// itself away once marked wrong, which is inherent to True/False.
function AttemptReviewDialog({
  attempt,
  onClose,
}: {
  attempt: AttemptSummary | null
  onClose: () => void
}) {
  const { data: review, isLoading } = useAttemptReview(attempt?.id)

  return (
    <Dialog open={attempt !== null} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-h-[85vh] max-w-2xl overflow-auto">
        <DialogHeader>
          <DialogTitle>
            {review ? review.quiz_title : "Attempt review"}
          </DialogTitle>
          <DialogDescription>
            {attempt && (
              <>
                Attempt {attempt.number}
                {attempt.score !== null && ` · ${attempt.score}%`}
                {` · ${attempt.passed ? "Passed" : "Not Passed"}`}
                {` · Version ${attempt.version_label}`}
              </>
            )}
          </DialogDescription>
        </DialogHeader>

        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="mr-2 h-5 w-5 animate-spin text-muted-foreground" />
            <span className="text-muted-foreground">Loading answers…</span>
          </div>
        ) : review ? (
          <div className="space-y-5">
            <p className="text-xs text-muted-foreground">
              Your answers are shown below, with the incorrect ones marked.
              Correct answers are not shown.
            </p>
            {review.questions.map((question, index) => (
              <div key={question.id} className="space-y-2">
                <div className="flex items-start justify-between gap-3">
                  {/* Numbered by place in the list, not by `position`:
                      questions authored through the admin editor all keep the
                      column's default of 0, which numbered every question
                      here "0.". The server already sends them ordered. */}
                  <p className="text-sm font-medium">
                    {index + 1}. {question.prompt}
                  </p>
                  {/* Green for correct, matching "Passed" everywhere else. */}
                  <Badge
                    className={cn(
                      "text-xs font-semibold",
                      question.correct
                        ? STATUS_CONFIG.passed.className
                        : STATUS_CONFIG.not_passed.className
                    )}
                  >
                    {question.correct ? "Correct" : "Incorrect"}
                  </Badge>
                </div>
                <ul className="space-y-1">
                  {question.answer_options.map((option) => {
                    const chosen =
                      option.id === question.selected_answer_option_id
                    return (
                      <li
                        key={option.id}
                        className={cn(
                          "rounded-md border px-3 py-2 text-sm",
                          chosen
                            ? question.correct
                              ? "border-emerald-300 bg-emerald-50 dark:border-emerald-900/50 dark:bg-emerald-900/20"
                              : "border-destructive/40 bg-destructive/5"
                            : "text-muted-foreground"
                        )}
                      >
                        {option.text}
                        {chosen && (
                          <span className="ml-2 text-xs text-muted-foreground">
                            (your answer)
                          </span>
                        )}
                      </li>
                    )
                  })}
                </ul>
              </div>
            ))}
          </div>
        ) : (
          <p className="py-6 text-center text-sm text-muted-foreground">
            These answers could not be loaded.
          </p>
        )}
      </DialogContent>
    </Dialog>
  )
}

export function MyTrainingPage() {
  const navigate = useNavigate()
  const { user } = useAuth()
  const { data: assignments = [], isLoading: assignmentsLoading } = useMyAssignments()
  const [expandedId, setExpandedId] = useState<number | null>(null)
  const [nameDialogOpen, setNameDialogOpen] = useState(false)

  const openRows = assignments.filter((r) => r.closed_at === null)
  const pastRows = assignments.filter((r) => r.closed_at !== null)

  const handleStartOrContinue = (row: StaffAssignmentRow) => {
    navigate({
      to: "/training/assessments/$assignmentId",
      params: { assignmentId: String(row.id) },
    })
  }

  const renderRow = (row: StaffAssignmentRow) => {
    const closed = row.closed_at !== null
    return (
                    <React.Fragment key={row.id}>
                      <TableRow>
                        <TableCell className="font-medium">
                          {row.quiz_title}
                          {row.self_assigned && (
                            <span
                              className="ml-1.5 text-[10px] text-muted-foreground"
                              title="Self-assigned"
                            >
                              (self)
                            </span>
                          )}
                        </TableCell>
                        <TableCell className="text-muted-foreground">
                          {formatDate(row.due_on)}
                        </TableCell>
                        <TableCell>
                          {closed ? (
                            <Badge className="border-transparent bg-secondary text-xs font-semibold text-secondary-foreground">
                              {row.closed_reason === "completed" ? "Completed" : "Not completed"}
                            </Badge>
                          ) : (
                            <>
                              <ResultBadge status={row.status} />
                              <AttemptsRemaining row={row} />
                            </>
                          )}
                        </TableCell>
                        <TableCell className="text-muted-foreground">
                          {row.score !== null ? `${row.score}%` : "—"}
                        </TableCell>
                        <TableCell>
                          <div className="flex flex-wrap items-center gap-2">
                            {!closed && row.status === "not_started" && (
                              <Button size="sm" onClick={() => handleStartOrContinue(row)}>
                                Start
                              </Button>
                            )}
                            {!closed && row.status === "in_progress" && (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleStartOrContinue(row)}
                              >
                                Continue
                              </Button>
                            )}
                            {!closed && row.can_retake && !row.locked && (
                              <Button
                                size="sm"
                                onClick={() => handleStartOrContinue(row)}
                              >
                                Retake
                              </Button>
                            )}
                            {row.status === "passed" && row.attempt_id && (
                              <Button asChild size="sm" variant="outline">
                                <Link
                                  to="/training/certificate/$attemptId"
                                  params={{ attemptId: String(row.attempt_id) }}
                                >
                                  View certificate
                                </Link>
                              </Button>
                            )}
                            {row.attempts_count > 0 && (
                              <Button
                                size="sm"
                                variant="ghost"
                                aria-expanded={expandedId === row.id}
                                onClick={() =>
                                  setExpandedId(expandedId === row.id ? null : row.id)
                                }
                              >
                                {expandedId === row.id
                                  ? "Hide history"
                                  : `History (${row.attempts_count})`}
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                      {expandedId === row.id && (
                        <TableRow>
                          <TableCell colSpan={5} className="bg-muted/30 p-0">
                            <AttemptHistory assignmentId={row.id} />
                          </TableCell>
                        </TableRow>
                      )}
                    </React.Fragment>
    )
  }

  const headerName = user?.full_name || user?.name

  return (
    <div className="space-y-6 overflow-auto p-6">
      <div>
        <div className="flex items-center gap-1.5">
          <h1 className="text-2xl font-semibold tracking-tight">
            Training for {headerName}
          </h1>
          {/* The name here is what prints on a certificate, so a typo has to
              be correctable by the one person who knows it is wrong — hence
              an edit entry point on the header itself, not just before the
              first assessment. */}
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7 text-muted-foreground hover:text-foreground"
            onClick={() => setNameDialogOpen(true)}
            aria-label="Edit name on certificates"
            title="Edit name on certificates"
          >
            <Pencil className="h-4 w-4" />
          </Button>
        </div>
        <p className="text-sm text-muted-foreground">
          View and complete your assigned competency assessments
        </p>
      </div>

      <FullNameDialog
        open={nameDialogOpen}
        initialValue={user?.full_name ?? ""}
        onOpenChange={setNameDialogOpen}
      />

      {/* Assigned to me */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Assigned to me</CardTitle>
          <CardDescription>Assessments you are required to complete</CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          {assignmentsLoading ? (
            <p className="px-6 pb-6 text-sm text-muted-foreground">Loading...</p>
          ) : assignments.length === 0 ? (
            <p className="px-6 pb-6 text-sm text-muted-foreground">
              No assessments assigned to you yet.
            </p>
          ) : (
            <div className="rounded-b-md border-t">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Assessment</TableHead>
                    <TableHead>Due</TableHead>
                    <TableHead className="w-[180px]">Status</TableHead>
                    <TableHead className="w-[80px]">Score</TableHead>
                    <TableHead className="w-[220px]">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {openRows.map((row) => renderRow(row))}
                  {pastRows.length > 0 && (
                    <TableRow className="hover:bg-transparent">
                      <TableCell colSpan={5} className="bg-muted/40 py-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                        Past rounds
                      </TableCell>
                    </TableRow>
                  )}
                  {pastRows.map((row) => renderRow(row))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
